import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { useState } from 'react'
import { useGetAreas } from 'api/areas'
import { openNewChat } from 'api/chats'
import { isUndefined } from 'lodash'
import clsx from 'clsx'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { InputRadioExpandable } from 'components/ui/InputRadioExpandable'
import { useModal } from 'providers/ModalProvider'

const schema = z.object({
  areaId: z.string({ error: 'validations.requiredField' }).min(1, 'validations.requiredField'),
})

type OpenChatFormValues = z.infer<typeof schema>

export const OpenChatModal = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { isAppMode } = useGetBotMode()
  const { closeModal } = useModal()

  const { data } = useGetAreas()

  const { control, handleSubmit, watch } = useForm<OpenChatFormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
  })

  const areaId = watch('areaId')

  const onSubmit = async (values: OpenChatFormValues) => {
    if (!values.areaId) return
    try {
      setLoading(true)
      await openNewChat({ areaId: values.areaId })
      setLoading(false)
      closeModal()
    } catch (e) {
      console.error({ e })
      setLoading(false)
    }
  }

  return (
    <Modal onClose={closeModal}>
      <ModalHeader title={t('selectAreaToOpenChat')} icon="comments" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className={clsx(isAppMode ? 'modal-container-app' : `modal-container`)}>
          <Controller
            name="areaId"
            control={control}
            render={({ field }) => (
              <div className="scrollbar-hidden flex flex-col overflow-y-auto">
                {data.map((area) => (
                  <div key={area._id} className={'mt-2 mr-2 border-b'}>
                    <InputRadioExpandable
                      key={area._id}
                      value={area._id}
                      label={area.name}
                      disabled={area.numberOfAreaSources === 0}
                      childrens={area.children}
                      selectedValue={field.value}
                      onChange={field.onChange}
                    />
                  </div>
                ))}
              </div>
            )}
          />
        </ModalBody>
        <ModalFooter>
          <OdinButton type={'submit'} className={'w-full'} loading={loading} disabled={isUndefined(areaId)}>
            <span className={'text-sm'}>{t('confirm')}</span>
          </OdinButton>
        </ModalFooter>
      </form>
    </Modal>
  )
}
